import { useEffect } from "react";

const SITE_NAME = "MarketOnBase";
export const SITE_URL = "https://marketonbase.com";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export interface PageMeta {
  /** Rendered as "{title} · MarketOnBase". Pass the bare page/listing title. */
  title: string;
  description: string;
  /** Path only, e.g. "/listings/abc123". Combined with the site origin for canonical + OG URLs. */
  path: string;
  /** Set false for authed/utility pages that shouldn't be indexed (checkout, wallet, orders, etc). */
  index?: boolean;
}

/**
 * This is a client-rendered SPA, so these updates only help crawlers that execute JS
 * (Googlebot does; link-unfurl bots for Slack/Discord/X generally do not — those read the
 * static tags baked into index.html at request time, which is why index.html's OG tags stay
 * generic rather than per-listing). This hook is for in-app tab correctness and JS-executing
 * crawlers, not a substitute for server-side rendering if per-listing social unfurls matter later.
 */
export function usePageMeta({ title, description, path, index = true }: PageMeta) {
  useEffect(() => {
    const fullTitle = `${title} · ${SITE_NAME}`;
    const url = `${SITE_URL}${path}`;

    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", index ? "index, follow" : "noindex, nofollow");
    upsertCanonical(url);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
  }, [title, description, path, index]);
}

/** Marks the script tags this hook owns, so it only ever removes its own on cleanup and never
 *  touches the static `WebSite` block baked into index.html. */
const JSONLD_MARKER = "data-page-jsonld";

/**
 * Injects page-scoped JSON-LD structured data and removes it on unmount, so navigating between
 * routes in this SPA can't leave a previous page's schema behind describing the wrong page.
 *
 * Pass a stable/memoized object — this re-serializes whenever the reference changes, so an inline
 * literal rebuilt on every render would thrash the DOM node.
 */
export function useJsonLd(data: object | null) {
  useEffect(() => {
    if (!data) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(JSONLD_MARKER, "");
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [data]);
}

/** Breadcrumb trail for a page, as Google's BreadcrumbList schema. `items` runs root-first. */
export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}
